// @flow
import React, { useEffect, useState } from "react";
import { push } from "connected-react-router";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { getRouteClientIdParam } from "@selectors";
import clientsActionCreators from "@modules/client/actionCreator";
import employeeActionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as clientsSelectors from "@modules/client/selectors";
import { Button, Pagination, GSGrid, View } from "@components";
import { toTableSortV2 } from "@transformers/sort";
import { route } from "@routes/helper";
import * as Paths from "@routes/constants";
import { ClientFilter, ClientModal } from "@modules/client/components";
import type { State } from "@store/reducers";
import type { Client } from "@modules/client/types";
import type { PaginationExtendedType } from "@types";
import type { ClientResponse } from "@modules/client/reducer/client/types";
import { hasPermission } from "@modules/account/selectors";

import "./styles.scss";
import { PermissionEnum } from "@modules/account/components";

type ClientItem = {
  ...Client,
  absentRate: number,
  headcount: number,
  absencesOverdueRate: number,
  absencesConsentWithheldRate: number,
  absencesWorkRelatedRate: number,
  tasksOverdueRate: number,
  appOnboardedRate: number,
  navigatorEnabled: boolean,
  monitoringEnabled: boolean,
  performanceScore: boolean,
  healthEnabled: boolean,
  developerApiAccess: boolean,
  mobileAppAccess: boolean,
  resellerName: string,
  managerName: string,
  industryName: string,
};

type PropsFromState = $Exact<{
  clientId: number,
  client?: Client,
  clients: Array<ClientResponse>,
  pagination: PaginationExtendedType,
  sort: Object,
  filterOptions: Object,
  showAddClientModal: boolean,
  hasHealthPermission: boolean,
}>;

type PropsFromDispatch = $Exact<{
  toggleAddClientModal: typeof clientsActionCreators.ToggleAddClientModal,
  getClients: typeof clientsActionCreators.GetClients,
  getClientsCsv: typeof clientsActionCreators.GetClientsCsv,
  onSort: typeof clientsActionCreators.SetClientsSort,
  createClient: typeof clientsActionCreators.AddClientDetails,
  getClientsSchema: typeof clientsActionCreators.GetClientsSchema,
  setEmployeesDefaultData: typeof employeeActionCreators.SetEmployeesDefaultData,
  resetActiveHierarchyTree: typeof sharedActionCreators.ResetMyHierarchyTree,
  navigate: typeof push,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ValueAndScore = {
  value: any,
  isScoring: boolean,
};

type ValuesAndScoreRecord = {
  health: ValueAndScore,
  monitoring: ValueAndScore,
  navigator: ValueAndScore,
  developer: ValueAndScore,
  mobileApp: ValueAndScore,
};

function ClientList(props: Props) {
  const { clients, filterOptions, pagination } = props;
  const [clientsList, setClientsList] = useState([]);

  // initial data load
  useEffect(() => {
    props.getClients();
    props.getClientsSchema();
  }, []);

  // setClientsList
  useEffect(() => {
    !!clients.length && setClientsList(clients);
  }, [clients]);

  const columns = [
    {
      key: "id",
      name: props.t("ID"),
      sortable: true,
    },
    {
      key: "name",
      name: props.t("NAME"),
      sortable: true,
      align: "left",
      renderCell: ({ row }: { row: ClientItem }) => GSGrid.render.ellipsis(row.name),
    },
    {
      key: "status",
      align: "center",
      name: props.t("STATUS"),
      width: 80,
      renderCell: ({ row }: { row: ClientItem }) => {
        let txtColor = row.status === "INACTIVE" ? "text-color-risk-very-high" : "";

        return <span className={txtColor}>{props.t(row.status)}</span>;
      },
      sortable: true,
    },
    {
      key: "absentRate",
      align: "center",
      name: props.t("PERCENT_ABSENT"),
      width: 80,
      renderCell: ({ row }: { row: ClientItem }) => {
        const { absentRate } = row;

        let bkgColor = "text-color-risk-medium";
        if (absentRate < 3.51) {
          bkgColor = "text-color-risk-low";
        } else if (absentRate > 4.49) {
          bkgColor = "text-color-risk-very-high";
        }

        return <strong className={`${bkgColor}`}>{`${row.absentRate}`}</strong>;
      },
    },
    {
      key: "headCount",
      name: props.t("UNIQUE_EMPLOYEES"),
      width: 80,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => row.headcount,
      onCellClick: (row: ClientItem) =>
        window.open(
          route("REPORTING_EMPLOYEES_SEARCH_PATH", {
            clientId: row.id,
          }),
          "_blank",
        ),
    },
    {
      key: "totalOpenedAbsences",
      name: props.t("OPEN_ABSENCES"),
      width: 80,
      align: "center",
      onCellClick: (row: ClientItem) =>
        window.open(
          route("REPORTING_REPORTS_ABSENCE_DETAILS_PATH", {
            clientId: row.id,
          }),
          "_blank",
        ),
    },
    {
      key: "totalOutstandingTasks",
      name: props.t("OPEN_TASKS"),
      width: 60,
      align: "center",
      onCellClick: (row: ClientItem) =>
        window.open(
          route("REPORTING_TOOLS_TASKS_PATH", {
            clientId: row.id,
          }),
          "_blank",
        ),
    },
    {
      key: "absencesOverdueRate",
      name: props.t("PERCENT_ABSENCES_OVERDUE"),
      tooltip: {
        text: props.t("PERCENT_ABSENCES_OVERDUE_TOOLTIP"),
      },
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) =>
        GSGrid.render.barIndicator({
          total: row.absencesOverdueRate || 0,
          max: 100,
          suffix: "%",
        }),
    },
    {
      key: "absencesConsentWithheldRate",
      name: props.t("PERCENT_CONSENT_WITHHELD"),
      tooltip: {
        text: props.t("PERCENT_CONSENT_WITHHELD_TOOLTIP"),
      },
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => `${row.absencesConsentWithheldRate}%`,
    },
    {
      key: "absencesWorkRelatedRate",
      name: props.t("PERCENT_WORK_RELATED"),
      tooltip: {
        text: props.t("PERCENT_WORK_RELATED_TOOLTIP"),
      },
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => `${row.absencesWorkRelatedRate}%`,
    },
    {
      key: "tasksOverdueRate",
      name: props.t("PERCENT_TASKS_OVERDUE"),
      tooltip: {
        text: props.t("PERCENT_TASKS_OVERDUE_TOOLTIP"),
      },
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) =>
        GSGrid.render.barIndicator({
          total: row.tasksOverdueRate || 0,
          max: 100,
          suffix: "%",
        }),
    },
    {
      key: "appOnboardedRate",
      name: props.t("PERCENT_APP_ADOPTION"),
      tooltip: {
        text: props.t("PERCENT_APP_ADOPTION_TOOLTIP"),
      },
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => `${row.appOnboardedRate}%`,
    },
    {
      key: "performanceScore",
      name: props.t("PERFORMANCE"),
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => {
        return GSGrid.render.scorePill(row.performanceScore);
      },
    },
    {
      key: "healthEnabled",
      name: props.t("HEALTH"),
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => {
        const health = getValuesAndScore(row).health;
        return getColorPillRender(health.value, health.isScoring);
      },
      onCellClick: (row: ClientItem) => {
        row.healthEnabled &&
          window.open(
            route("REPORTING_WORKFORCE_HEALTH_PATH", {
              clientId: row.id,
            }),
            "_blank",
          );
      },
    },
    {
      key: "monitoringEnabled",
      name: props.t("MONITORING"),
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => {
        const monitoring = getValuesAndScore(row).monitoring;
        return getColorPillRender(monitoring.value, monitoring.isScoring);
      },
      onCellClick: (row: ClientItem) => {
        row.monitoringEnabled &&
          window.open(
            route("ADMINISTRATION_SERVICE_FEATURES_MONITOR", {
              clientId: row.id,
            }),
            "_blank",
          );
      },
    },
    {
      key: "navigatorEnabled",
      name: props.t("NAVIGATOR"),
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => {
        const navigator = getValuesAndScore(row).navigator;
        return getColorPillRender(navigator.value, navigator.isScoring);
      },
      onCellClick: (row: ClientItem) => {
        row.navigatorEnabled &&
          window.open(
            route("ADMINISTRATION_SERVICE_FEATURES_NAVIGATOR", {
              clientId: row.id,
            }),
            "_blank",
          );
      },
    },
    {
      key: "developerApiAccess",
      name: props.t("DEVELOPER"),
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => {
        const developer = getValuesAndScore(row).developer;
        return getColorPillRender(developer.value, developer.isScoring);
      },
    },
    {
      key: "mobileAppAccess",
      name: props.t("APP"),
      width: 90,
      align: "center",
      renderCell: ({ row }: { row: ClientItem }) => {
        const mobileApp = getValuesAndScore(row).mobileApp;
        return getColorPillRender(mobileApp.value, mobileApp.isScoring);
      },
      onCellClick: (row: ClientItem) => {
        row.mobileAppAccess &&
          window.open(
            route("ADMINISTRATION_SERVICE_EXISTING_USERS", {
              clientId: row.id,
            }),
            "_blank",
          );
      },
    },
    {
      key: "managerName",
      name: props.t("MANAGER"),
      sortable: true,
      align: "left",
      renderCell: ({ row }: { row: ClientItem }) => GSGrid.render.ellipsis(row.managerName),
    },
    {
      key: "industryName",
      name: props.t("INDUSTRY"),
      sortable: true,
      align: "left",
      renderCell: ({ row }: { row: ClientItem }) => GSGrid.render.ellipsis(row.industryName),
    },
    {
      key: "resellerName",
      name: props.t("OWNER"),
      sortable: true,
      align: "left",
      renderCell: ({ row }: { row: ClientItem }) => GSGrid.render.ellipsis(row.resellerName),
    },

    {
      name: "ACTION_MENU",
      frozen: "right",
      contextMenuConfig: (row: ClientItem) => [
        {
          label: props.t("REPORTS"),
          onClick: () => onItemClick("REPORTING_DASHBOARD_PATH", row),
        },
        {
          label: props.t("SLA_PERFORMANCE"),
          onClick: () => onItemClick("ADMINISTRATION_SLA_PERFORMANCE_PATH", row),
        },
      ],
    },
  ];

  function fetchClients(page: number) {
    props.getClients({ page });
  }

  function toggleEditModal() {
    props.toggleAddClientModal(!props.showAddClientModal);
  }

  function onItemClick(path: $Keys<typeof Paths>, item: ClientItem) {
    if (!props.client || props.client.id !== item.id) {
      props.setEmployeesDefaultData();
      props.resetActiveHierarchyTree();
    }
    props.navigate(route(path, { clientId: item.id }));
  }

  function onSort(sort: Object) {
    props.onSort({
      sort,
    });
  }

  function getValuesAndScore(row: ClientItem): ValuesAndScoreRecord {
    const health = props.t(row.healthEnabled ? "YES" : "NO");
    const monitoring = props.t(row.monitoringEnabled ? "YES" : "NO");
    const navigator = props.t(row.navigatorEnabled ? "YES" : "NO");
    const developer = props.t(row.developerApiAccess ? "YES" : "NO");
    const mobileApp = props.t(row.mobileAppAccess ? "YES" : "NO");

    return {
      health: { value: health, isScoring: row.healthEnabled },
      monitoring: { value: monitoring, isScoring: row.monitoringEnabled },
      navigator: { value: navigator, isScoring: row.navigatorEnabled },
      developer: { value: developer, isScoring: row.developerApiAccess },
      mobileApp: { value: mobileApp, isScoring: row.mobileAppAccess },
    };
  }

  function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
    return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
  }

  return (
    <div className="clients-list gs-container">
      <View direction="row">
        <ClientFilter
          onSubmit={filters =>
            props.getClients({
              filters,
              page: 1,
            })
          }
          options={filterOptions}
          onReset={fetchClients}
        />

        <View direction="row" alignItems="center">
          <Button key="export" materialIcon="file_download" onClick={props.getClientsCsv}>
            {props.t("EXPORT")}
          </Button>
          <Button
            key="add-new-client"
            className="ml-2"
            materialIcon="add_circle"
            onClick={toggleEditModal}
            color="active"
          >
            {props.t("ADD_CLIENT")}
          </Button>
        </View>
      </View>
      <View>
        <GSGrid
          rows={clientsList}
          columns={columns}
          onSortChange={onSort}
          sortColumns={toTableSortV2(props.sort)}
          height={"65vh"}
          // maxHeight={"100%"}
        />

        <View direction="row" justifyContent="center">
          <Pagination
            currentPage={pagination.pageNumber}
            totalPages={pagination.totalPages}
            onChange={fetchClients}
          />
        </View>
      </View>
      {props.showAddClientModal ? (
        <ClientModal
          clientId={props.clientId}
          onSubmit={props.createClient}
          onClose={toggleEditModal}
        />
      ) : null}
    </div>
  );
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  const clientId = getRouteClientIdParam(ownProps);
  return {
    clientId,
    client: clientsSelectors.getClient(state),
    clients: clientsSelectors.getClients(state),
    filterOptions: clientsSelectors.getFilterOptions(state),
    sort: clientsSelectors.getClientsSort(state),
    pagination: clientsSelectors.getClientsPagination(state),
    showAddClientModal: clientsSelectors.getShowAddClientModal(state),
    hasHealthPermission: hasPermission(state, PermissionEnum.ACCESS_REPORTING_HEALTH),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getClients: clientsActionCreators.GetClients,
  onSort: clientsActionCreators.SetClientsSort,
  getClientsCsv: clientsActionCreators.GetClientsCsv,
  toggleAddClientModal: clientsActionCreators.ToggleAddClientModal,
  createClient: clientsActionCreators.AddClientDetails,
  getClientsSchema: clientsActionCreators.GetClientsSchema,
  setEmployeesDefaultData: employeeActionCreators.SetEmployeesDefaultData,
  resetActiveHierarchyTree: sharedActionCreators.ResetMyHierarchyTree,
  navigate: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(ClientList));
